import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { login as apiLogin, register as apiRegister, logout as apiLogout } from '@/api/auth';
import type { LoginCredentials, RegisterData } from '@/types';

export function useAuth() {
  const navigate = useNavigate();
  const hydrate = useAuthStore((s) => s.hydrate);
  const clearAuth = useAuthStore((s) => s.logout);
  const { isAuthenticated, plan, role, tenantId, tenantSlug, email } = useAuthStore();

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      await apiLogin(credentials); // sets cookies
      const ok = await hydrate();  // populate identity from /auth/me
      if (!ok) throw new Error('Login succeeded but session could not be established');
      navigate('/dashboard');
    },
    [hydrate, navigate]
  );

  const register = useCallback(
    async (data: RegisterData) => {
      await apiRegister(data); // sets cookies
      const ok = await hydrate();
      if (!ok) throw new Error('Registration succeeded but session could not be established');
      navigate('/dashboard');
    },
    [hydrate, navigate]
  );

  const logout = useCallback(async () => {
    await apiLogout();
    clearAuth();
    navigate('/login');
  }, [clearAuth, navigate]);

  return {
    login,
    register,
    logout,
    isAuthenticated,
    plan,
    role,
    tenantId,
    tenantSlug,
    email,
  };
}
